import { FC } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { Placeholder } from 'rsuite'

import { useApi } from '../../../../services/useApi'
import { Supplier } from '../../../../utils/types/Product'
import { getSmallPreview, imageLoader } from '../../../../utils/images'
import useUrls from '../../../../services/useUrls'
import { ResourceTag } from '../../../../utils/constants'
import { getCountryAndLocaleStrings } from '../../../../utils/locales'
import { getSupplierDeliveryDetails } from '../../../../lib/service'
import FallBackCategoryImg from '../../../Graphics/FallBackCategoryImg'
import { MegaMenuFormattedData } from '../../../../utils/types/strapi'
import { getSupplierLogo } from '../../../../lib/supplier'
import { createUrlParams } from '../../../../lib/params'

import styles from '../../../../styles/MegaMenuSupplierIconsRow.module.less'

interface MegaMenuSupplierIconsRowProps {
  closeMegaMenu: () => void
  columnData?: MegaMenuFormattedData

}

const MegaMenuSupplierIconsRow: FC<MegaMenuSupplierIconsRowProps> = ({
  closeMegaMenu,
  columnData,
}) => {
  const { getResource } = useApi()
  const { urlT } = useUrls()
  const { locale } = useRouter()

  const { country } = getCountryAndLocaleStrings(locale)

  // Extract supplier codes from URLs using regex instead of using labels
  const popularBrandSupplierCodes = columnData?.popularBrandsColumn?.linkData
    .map((item) => item.link?.split('/')?.at(1) || '')
    .filter(Boolean) || []

  // Build API query using createUrlParams with supplier codes
  const apiQuery = createUrlParams({
    include: ['media', 'text', 'supplier/address', 'supplier/service'],
    sort: 'supplier.address.position',
    page: {
      limit: '10',
    },
    ...(popularBrandSupplierCodes.length > 0 && {
      filter: {
        '||': popularBrandSupplierCodes.map((supplierCode) => ({
          '==': { field: 'supplier.code', value: supplierCode },
        })),
      },
    }),
  })

  const { data: suppliersUnfiltered, isLoading } = getResource<Supplier>(
    ResourceTag.supplier,
    apiQuery,
  )

  const suppliers = suppliersUnfiltered
    .filter((supplier) => getSupplierDeliveryDetails(supplier, country).isAvailable)

  if (isLoading) {
    return (
      <div className={styles['supplier-logos-container']}>
        {Array.from({ length: Math.max(popularBrandSupplierCodes.length, 3) }, (_, i) => (
          <div
            key={i}
            className={styles['supplier-logo-item']}
          >
            <Placeholder.Graph
              active
              className={styles['skeleton-supplier-logo']}
            />
          </div>
        ))}
      </div>
    )
  }

  if (suppliers.length === 0) {
    return null
  }

  return (
    <div className={styles['supplier-logos-container']}>
      {suppliers.map((supplier) => {
        const supplierName = supplier['supplier.label']
        const supplierCode = supplier['supplier.code']
        const imageAlt = `${supplierName} logo`
        const supplierLogo = getSupplierLogo(supplier.media)

        return (
          <Link
            key={supplierCode}
            href={urlT(`/supplier/${supplierCode}`)}
            onClick={() => closeMegaMenu()}
          >
            <div className={styles['supplier-logo-item']}>
              {supplier.media?.[0]['media.url'] ? (
                <div className={styles['logo-container']}>
                  <Image
                    loader={imageLoader}
                    src={getSmallPreview(supplierLogo)}
                    alt={imageAlt}
                    layout="fill"
                    objectFit="contain"
                  />
                </div>
              ) : (
                <div>
                  <FallBackCategoryImg height="80" />
                </div>
              )}
            </div>
          </Link>
        )
      })}
    </div>
  )
}

export default MegaMenuSupplierIconsRow
